import React from 'react';
import { Check, CheckCheck, Signal, Wifi, Battery } from 'lucide-react';

/**
 * WaEmulator — reusable WhatsApp chat preview component.
 * Props:
 *   message: string (the outgoing message text)
 *   senderName: string (shown in contact bar, default "Pelanggan")
 *   timestamp: Date | string | null
 */
export default function WaEmulator({ message = '', senderName = 'Pelanggan', timestamp = null }) {
  const now = timestamp ? new Date(timestamp) : new Date();
  const timeStr = now.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });

  // Convert WhatsApp-style *bold* and \n to HTML
  const formatMessage = (text) => {
    if (!text) return '';
    return text
      .replace(/\*(.*?)\*/g, '<strong>$1</strong>')
      .replace(/_(.*?)_/g, '<em>$1</em>')
      .replace(/\n/g, '<br/>');
  };

  return (
    <div className="relative w-full max-w-[300px] mx-auto select-none">
      {/* Phone shell */}
      <div className="bg-[#111] rounded-[32px] p-2.5 shadow-2xl border border-white/10">
        {/* Status bar */}
        <div className="flex items-center justify-between px-4 pt-1 pb-0.5">
          <span className="text-white text-[9px] font-semibold">{timeStr}</span>
          <div className="flex items-center gap-1">
            <Signal size={9} className="text-white" />
            <Wifi size={9} className="text-white" />
            <Battery size={9} className="text-white" />
          </div>
        </div>

        {/* WA screen */}
        <div className="rounded-[22px] overflow-hidden" style={{ height: '440px', display: 'flex', flexDirection: 'column' }}>
          {/* WA Header */}
          <div className="bg-[#075E54] px-3 py-2.5 flex items-center gap-2.5 flex-shrink-0">
            <div className="w-7 h-7 rounded-full bg-emerald-400 flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
              {senderName.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-xs font-semibold truncate">{senderName}</p>
              <p className="text-emerald-200 text-[9px]">online</p>
            </div>
          </div>

          {/* Chat background */}
          <div
            className="flex-1 overflow-y-auto px-2 py-3 flex flex-col justify-end gap-1"
            style={{
              background: '#e5ddd5',
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23b0a898' fill-opacity='0.15'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          >
            {message ? (
              <div className="flex justify-end">
                <div
                  className="max-w-[85%] bg-[#DCF8C6] rounded-tl-2xl rounded-bl-2xl rounded-tr-sm rounded-br-2xl px-3 py-2 shadow-sm"
                  style={{ position: 'relative' }}
                >
                  {/* Bubble tail */}
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      right: -6,
                      width: 0,
                      height: 0,
                      borderStyle: 'solid',
                      borderWidth: '0 0 8px 8px',
                      borderColor: 'transparent transparent transparent #DCF8C6',
                    }}
                  />
                  <p
                    className="text-[11px] text-[#303030] leading-relaxed break-words"
                    dangerouslySetInnerHTML={{ __html: formatMessage(message) }}
                  />
                  <div className="flex items-center justify-end gap-1 mt-0.5">
                    <span className="text-[9px] text-[#667781]">{timeStr}</span>
                    <CheckCheck size={11} className="text-[#4FC3F7]" />
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-[10px] text-[#8696a0] bg-white/60 rounded-full px-3 py-1">
                  Ketik pesan untuk melihat preview
                </p>
              </div>
            )}
          </div>

          {/* Input bar */}
          <div className="bg-[#F0F0F0] px-2 py-1.5 flex items-center gap-1.5 flex-shrink-0">
            <div className="flex-1 bg-white rounded-full px-3 py-1.5">
              <p className="text-[9px] text-gray-400">Pesan</p>
            </div>
            <div className="w-7 h-7 rounded-full bg-[#25D366] flex items-center justify-center flex-shrink-0">
              <Check size={12} className="text-white" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
